import { SignupFormData } from './types'
import { AddressFieldSchema, CheckboxFieldSchema, DateOfBirthFieldSchema, FieldType, InputFieldSchema, PhoneFieldSchema, Schema } from '@afterpaytouch/form'
import { TFunction } from 'next-i18next'
import {
  dobValidation,
  namePattern,
  nameValidation,
  nameValidationWithLength,
  requiredAddress,
  requiredValidation,
  validatePoBox,
  validateState,
} from '../ProfileForm/validation'
import { Country } from '@afterpaytouch/portal-api/types'
import { ConsumerState } from '../../state'
import { app } from '../../env'
import { InputAddressValidationRule, InputBirthdayDateFormatPosition } from '@afterpaytouch/core'
import { getAddressOptionalFields } from '../../utils/profile-validation'
import { isValidName } from '../../utils/validation'
import { ALLOWED_ADDRESS_TYPES } from './constants'
import { isValidEmail } from '@afterpay/utils'
import { SupportedLocale } from '@afterpaytouch/shop-api'
import { ConsumerAddress } from '@afterpay/types'
import { LogEvent, TrackingEvent } from '../../model/amplitude'

interface GetSchemaAndFormDataResult {
  schema: Schema<SignupFormData>
  initialFormData: SignupFormData
}

export const getFormData = (
  consumer: ConsumerState,
  givenNames: string,
  surname: string,
  defaultMobile: string,
  isMobileLogin: boolean,
  consumerMobile: string | undefined,
  parseMobile: any,
  country: Country
): SignupFormData => {
  const addressData = {
    address1: consumer.contactAddress?.address1 ?? '',
    address2: consumer.contactAddress?.address2 ?? '',
    area2: consumer.contactAddress?.area2 ?? '',
    suburb: consumer.contactAddress?.suburb ?? '',
    state: consumer.contactAddress?.state ?? '',
    postcode: consumer.contactAddress?.postcode ?? '',
    countryCode: consumer.contactAddress?.countryCode ?? '',
  }
  return {
    profileCard: undefined,
    givenNames: isValidName({ name: consumer.givenNames ?? givenNames }) ? consumer.givenNames ?? givenNames : '',
    otherGivenNames: consumer.otherGivenNames ?? '',
    surname: isValidName({ name: consumer.surname ?? surname }) ? consumer.surname ?? surname : '',
    mobile: isMobileLogin ? parseMobile(consumerMobile) : defaultMobile,
    mobileTerms: undefined,
    dateOfBirth: consumer.dateOfBirth ?? '',
    email: isValidEmail(consumer.email ?? '') ? consumer.email ?? '' : '',
    address: addressData,
    marketingOptIn: country === Country.GB ? Boolean(consumer.marketingOptIn) ?? false : undefined,
    terms: undefined,
    inlineError: undefined,
    continueButton: undefined,
  }
}

const getInputFieldSchema = ({ value, label, error = false, options = {}, show = true, otherProps = {}, inline = false }): InputFieldSchema => ({
  type: FieldType.Input,
  show,
  props: {
    label,
    value,
    error,
    ...otherProps,
  },
  options,
  inline,
})

const getCheckboxFieldSchema = ({ value, error = false, otherProps = {}, options = {}, show = true }): CheckboxFieldSchema => ({
  type: FieldType.Checkbox,
  show,
  props: {
    defaultChecked: value,
    ...otherProps,
  },
  options,
})

const getInputBirthdaySchema = ({ locale, label, error = false, value, options, otherValidations = {}, otherProps = {} }): DateOfBirthFieldSchema => ({
  type: FieldType.DateOfBirth,
  show: true,
  props: {
    label,
    defaultValue: value,
    locale: locale as SupportedLocale,
    error,
    ...otherProps,
  },
  options,
  ...otherValidations,
})

const getPhoneFieldSchema = ({
  value,
  error = false,
  otherProps = {},
  options = {},
  otherValidations = {},
  validateOnLoad = false,
  show = true,
}): PhoneFieldSchema => ({
  type: FieldType.Phone,
  show,
  validateOnLoad,
  props: {
    value,
    error,
    ...otherProps,
  },
  options,
  ...otherValidations,
})

const getAddressFieldSchema = ({ value, error = false, otherProps = {}, options = {}, validateOnLoad = false, disabled, show = true }): AddressFieldSchema => ({
  type: FieldType.Address,
  apiKey: app.GOOGLEMAPS_API_KEY,
  show,
  validateOnLoad,
  props: {
    initialAddress: value,
    error,
    disabled,
    ...otherProps,
  },
  options,
})
export interface GetSchemaRequest {
  components: any
  t: TFunction
  givenNames: string
  surname: string
  enableEditName: boolean
  consumer: ConsumerState
  locale: SupportedLocale
  country: Country
  minAge: number
  defaultMobile: string
  isMobileLogin: boolean
  parseMobile: any
  supportedPrefixes: Country[]
  initialAddresses: ConsumerAddress[] | ConsumerAddress | undefined
  isStateEligibleForPayByInstallmentEnabled: boolean
  handleGeocodeResult: (success: boolean) => void
  showDobInfo: boolean
  showInlineError: boolean
  logEvent: LogEvent
  isCashTheme: boolean
}
export const getSchema = ({
  components,
  t,
  givenNames,
  surname,
  enableEditName,
  consumer,
  locale,
  country,
  minAge,
  defaultMobile,
  isMobileLogin,
  parseMobile,
  supportedPrefixes,
  initialAddresses,
  isStateEligibleForPayByInstallmentEnabled,
  handleGeocodeResult,
  showDobInfo,
  showInlineError,
  logEvent,
  isCashTheme,
}: GetSchemaRequest): GetSchemaAndFormDataResult => {
  const { phoneNumber: consumerMobile } = consumer
  const initialFormData = getFormData(consumer, givenNames, surname, defaultMobile, isMobileLogin, consumerMobile, parseMobile, country)
  const { givenNames: initalGivenames, otherGivenNames, surname: initialSurname, dateOfBirth, mobile, address, marketingOptIn } = initialFormData
  const required = requiredValidation(t)
  const namePatterns = namePattern(t)
  const dobValidateOptions = dobValidation(t, minAge)
  const allowedCountries = supportedPrefixes.map((country) => {
    return {
      countryCode: country,
      name: t('common:countryName', { context: country }),
    }
  })

  const schema: Schema<SignupFormData> = {
    givenNames: getInputFieldSchema({
      label: t('signUp:firstNameLabel'),
      value: initalGivenames,
      show: enableEditName,
      otherProps: {
        capitalize: true,
        'data-dd-action-name': 'Create Account First Name Input',
      },
      options: {
        ...required('givenNames'),
        ...namePatterns('givenNames'),
        ...nameValidationWithLength({ name: 'givenNames', country, t }),
      },
      inline: true,
    }),
    otherGivenNames: getInputFieldSchema({
      label: t('signUp:middleNameLabel'),
      value: otherGivenNames,
      show: enableEditName,
      otherProps: {
        capitalize: true,
        'data-dd-action-name': 'Create Account Middle Name Input',
      },
      options: {
        ...namePatterns('otherGivenNames'),
        ...nameValidation(t),
      },
      inline: true,
    }),
    surname: getInputFieldSchema({
      label: t('signUp:lastNameLabel'),
      value: initialSurname,
      show: enableEditName,
      otherProps: {
        capitalize: true,
        'data-dd-action-name': 'Create Account Last Name Input',
      },
      options: {
        ...required('surname'),
        ...namePatterns('surname'),
        ...nameValidationWithLength({ name: 'surname', country, t }),
      },
    }),
    email: getInputFieldSchema({
      label: t('common:email'),
      value: '',
      error: false,
      show: false,
    }),
    mobile: getPhoneFieldSchema({
      value: mobile,
      show: true,
      otherProps: {
        label: t('signUp:mobileInputLabel'),
        allowedCountries: allowedCountries.length > 0 ? allowedCountries : undefined,
        selectedCountry: country,
        'data-dd-action-name': 'Create Account Mobile Input',
      },
      options: {
        ...required('mobile'),
      },
      otherValidations: {
        invalid: t('signUp:error.invalidMobile'),
      },
    }),

    dateOfBirth: getInputBirthdaySchema({
      value: dateOfBirth,
      label: t('signUp:dateOfBirthLabel'),
      locale,
      options: {
        ...required('dateOfBirth'),
      },
      otherProps: {
        dateFormatPosition: InputBirthdayDateFormatPosition.SecondaryLabel,
        infoText: showDobInfo ? t('signUp:dateOfBirthInfo') : undefined,
        hasDateFormatLabel: !isCashTheme,
        'data-dd-action-name': 'Create Account Date Of Birth Input',
      },
      otherValidations: dobValidateOptions('dateOfBirth'),
    }),
    address: getAddressFieldSchema({
      value: address,
      disabled: false,
      show: true,
      otherProps: {
        defaultExpanded: false,
        label: t('signUp:address'),
        manualAddressLabel: t('signUp:findAddressLabel'),
        autoFocus: false,
        address1Label: t('signUp:address1Label'),
        address2Label: t('signUp:address2Label'),
        postcodeLabel: t('signUp:postcodeLabel'),
        componentRestrictions: { country },
        onGeocodeResult: handleGeocodeResult,
        focusOnSelect: false,
        initialAddress: initialAddresses,
        suburbLabel: t('signUp:suburbLabel'),
        errorText: t('signUp:error.blankAddress'),
        stateLabel: t('signUp:stateLabel'),
        searchAddressLabel: t('signUp:searchAddressLabel'),
        optionalManualAddressFields: getAddressOptionalFields(country),
        onManualAddressLabelClick: () => logEvent(TrackingEvent.PRESSED_MANUAL_ADDRESS_TOGGLE),
        allowedAddressType: ALLOWED_ADDRESS_TYPES,
        validateRule: InputAddressValidationRule.ByAddress,
        'data-dd-action-name': 'Create Account Address Input',
      },
      options: {
        validate: {
          ...requiredAddress({ name: 'address', country, t }),
          ...validatePoBox(t('signUp:error.poBoxError')),
          ...validateState(isStateEligibleForPayByInstallmentEnabled, t),
        },
      },
    }),
    marketingOptIn: getCheckboxFieldSchema({
      value: marketingOptIn,
      show: country === Country.GB,
      otherProps: {
        label: components.marketingOptIn,
        size: 'Small',
        id: 'marketingOptInCheckbox',
        'data-dd-action-name': 'Create Account Marketing Opt In Checkbox',
      },
    }),
    terms: {
      type: FieldType.Component,
      Component: components.terms,
    },
    inlineError: {
      type: FieldType.Component,
      show: showInlineError,
      Component: components.inlineError,
    },
    continueButton: {
      type: FieldType.Component,
      Component: components.continueButton,
    },
  }
  return { schema, initialFormData }
}
